import React from 'react';
import {
  Plus, Landmark, CreditCard, Building2, Eye, EyeOff, Check, Trash2,
} from 'lucide-react';
import { maskAccountNumber } from './utils';

function BankAccountCard({
  account,
  isNumberVisible,
  onToggleVisibility,
  onSetPrimary,
  onDelete,
}) {
  const primaryChip = account.is_primary ? 'bg-emerald-100' : 'bg-gray-100';
  const primaryIconColor = account.is_primary ? 'text-emerald-600' : 'text-gray-600';

  return (
    <div
      className={`p-5 rounded-xl border-2 transition ${
        account.is_primary ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
      data-testid={`bank-account-${account._id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${primaryChip}`}>
            {account.card_type
              ? <CreditCard className={`w-6 h-6 ${primaryIconColor}`} />
              : <Building2 className={`w-6 h-6 ${primaryIconColor}`} />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900">{account.bank_name}</h4>
              {account.is_primary && (
                <span className="px-2 py-0.5 bg-emerald-200 text-emerald-800 rounded-full text-xs font-medium">
                  Primary
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 capitalize">{account.account_type} Account</p>

            <div className="mt-2 space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Account:</span>
                <span className="font-mono">
                  {isNumberVisible ? account.account_number : maskAccountNumber(account.account_number)}
                </span>
                <button
                  type="button"
                  onClick={onToggleVisibility}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  {isNumberVisible ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Routing:</span>
                <span className="font-mono">{account.routing_number}</span>
              </div>
              {account.card_type && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Card:</span>
                  <span className="capitalize">{account.card_type} •••• {account.card_last_four}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!account.is_primary && (
            <button
              type="button"
              onClick={onSetPrimary}
              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
              title="Set as primary"
            >
              <Check className="w-5 h-5" />
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
            title="Remove account"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Bank Accounts tab body. Shows an empty state or the list of the
 * worker's linked bank accounts.
 */
export default function BankAccountsList({
  bankAccounts,
  showAccountNumbers,
  onToggleShowAccount,
  onSetPrimary,
  onDelete,
  onAddAccount,
}) {
  if (bankAccounts.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Your Bank Accounts</h3>
          <button
            type="button"
            onClick={onAddAccount}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition"
            data-testid="add-new-account-btn"
          >
            <Plus className="w-4 h-4" />
            Add Account
          </button>
        </div>
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Landmark className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">No Bank Accounts</h3>
          <p className="text-gray-500 text-sm mb-4">Add a bank account to transfer funds</p>
          <button
            type="button"
            onClick={onAddAccount}
            className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition"
          >
            Add Your First Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Your Bank Accounts</h3>
        <button
          type="button"
          onClick={onAddAccount}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition"
          data-testid="add-new-account-btn"
        >
          <Plus className="w-4 h-4" />
          Add Account
        </button>
      </div>

      <div className="space-y-3">
        {bankAccounts.map((account) => (
          <BankAccountCard
            key={account._id}
            account={account}
            isNumberVisible={!!showAccountNumbers[account._id]}
            onToggleVisibility={() => onToggleShowAccount(account._id)}
            onSetPrimary={() => onSetPrimary(account._id)}
            onDelete={() => onDelete(account._id)}
          />
        ))}
      </div>
    </div>
  );
}
